import { LoadDuration } from "./types/loadDuration";
import { Screw } from "./types/screw";
import { TimberClass } from "./types/timberClass";

export const RHO_ALUMINUM = 385; // reference density for the withdrawal/head-pull-through formula
export const ROPE_EFFECT = 1; // rope effect always at 100%
export const SCREW_HEAD_COEFFICIENT = 10.5; // f_head,k, identical for every HBS screw

// EC5 table 3.1 — k_mod for solid and glued laminated timber, by service class and load duration.
export const KMOD: Record<1 | 2 | 3, Record<LoadDuration, number>> = {
  1: { perm: 0.6, long: 0.7, medium: 0.8, short: 0.9, inst: 1.1 },
  2: { perm: 0.6, long: 0.7, medium: 0.8, short: 0.9, inst: 1.1 },
  3: { perm: 0.5, long: 0.55, medium: 0.65, short: 0.7, inst: 0.9 },
};

// EC5 verification of an existing timber beam reinforced with an HBS wood screw
// connection, ported 1:1 from the original engineering tool (Johansen yield modes,
// bending/shear utilization, screw withdrawal/head-pull-through capacity).
// All values are computed client-side — nothing here needs server-only data.

export type BeamReinforcementInput = {
  serviceClass: 1 | 2 | 3;
  loadDuration: LoadDuration;
  tipTolerance: number; // mm
  existingMaterial: TimberClass;
  existingBase: number; // mm
  existingHeight: number; // mm
  reinforcementMaterial: TimberClass;
  reinforcementBase: number; // mm
  reinforcementHeight: number; // mm
  reinforcementSides: 1 | 2;
  permanentLoad: number; // kN/m
  liveLoad: number; // kN/m
  beamLength: number; // m
  screw: Screw;
  screwSpacing: number; // mm
  preDrilled: boolean;
};

export type JohansenResult = {
  beta: number;
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
  min: number;
};

export type BeamReinforcementResult = {
  kmod: number;
  existing: TimberClass;
  reinforcement: TimberClass;
  screw: Screw;

  // Raw geometry, echoed back for convenience (drawing, calculation details)
  b1: number;
  h1: number;
  b2: number;
  h2: number;
  n1: 1 | 2;
  screwSpacing: number;
  tipTolerance: number;
  preDrilled: boolean;

  g1: number; // kN/m — automatic self-weight
  qd: number; // N/mm — ULS distributed load

  i1: number;
  i2: number;
  nu1: number;
  nu2: number;

  md: number; // N*mm
  md1: number;
  md2: number;
  w1: number;
  w2: number;
  sm1: number; // MPa
  sm2: number;
  fmd1: number;
  fmd2: number;
  etaM1: number; // %
  etaM2: number;
  etaMmax: number;

  vd: number; // N
  vd1: number;
  vd2: number;
  tau1: number; // MPa
  tau2: number;
  fvd1: number;
  fvd2: number;
  etaV1: number; // %
  etaV2: number;
  etaVmax: number;

  // Screw fit and connection
  t1: number;
  lt2: number;
  t2: number;
  leqreq: number;
  lef: number;
  a1min: number;
  a3c: number;
  a4t: number;
  a4c: number;
  stag: number;
  hmin: number;
  lmax: number;
  heightOK: boolean;
  geomOK: boolean;
  eBottom: number;
  eTop2: number;
  centered: boolean;

  fh1: number;
  fh2: number;
  johansen: JohansenResult | null;
  fvRd: number; // N
  fvRk: number;

  fHead: number; // N
  fThr: number;
  faxRk: number;

  nvBottom: number;
  nvTop: number;
  nvTotal: number;

  vEd: number; // N
  etaConn: number; // %

  a4tEff: number;
  a4cEff: number;
  a4tOK: boolean;
  a4cOK: boolean;

  spacOK: boolean;
  loadOK: boolean;
  screwOK: boolean;
};

const clamp0 = (x: number): number => Math.max(0, x);

const fhk = (density: number, d: number, preDrilled: boolean): number => {
  const x = preDrilled ? 1 - 0.01 * d : d;
  return 0.082 * density * Math.pow(x, -0.3);
};

const johansen = (
  fh1: number,
  fh2: number,
  t1: number,
  t2: number,
  d: number,
  my: number,
  faxRk: number,
): JohansenResult => {
  const beta = fh2 / fh1;
  const r = t2 / t1;
  const a = fh1 * t1 * d;
  const b = fh2 * t2 * d;
  const c1 =
    ((fh1 * t1 * d) / (1 + beta)) *
    (Math.sqrt(beta + 2 * beta * beta * (1 + r + r * r) + Math.pow(beta, 3) * r * r) - beta * (1 + r));
  const d1 =
    ((1.05 * fh1 * t1 * d) / (1 + beta)) *
    (Math.sqrt(2 * beta * (1 + beta) + (4 * beta * (2 + beta) * my) / (fh1 * d * t1 * t1)) - beta);
  const e1 =
    ((1.05 * fh1 * t2 * d) / (1 + 2 * beta)) *
    (Math.sqrt(2 * beta * beta * (1 + beta) + (4 * beta * (1 + 2 * beta) * my) / (fh1 * d * t2 * t2)) - beta);
  const f1 = 1.15 * Math.sqrt((2 * beta) / (1 + beta)) * Math.sqrt(2 * my * fh1 * d);
  const rope = (x: number): number => x + Math.min((faxRk / 4) * ROPE_EFFECT, x);
  const c = rope(c1);
  const d2 = rope(d1);
  const e = rope(e1);
  const f = rope(f1);

  return { beta, a, b, c, d: d2, e, f, min: Math.min(a, b, c, d2, e, f) };
};

const axial = (screw: Screw, lef: number, reinforcementRho: number): { fHead: number; fThr: number; faxRk: number } => {
  const dh = screw.screwFamily.headDiameter;
  const d = screw.screwFamily.diameter;
  const ft = screw.screwFamily.tensileCapacity;
  const fHead = SCREW_HEAD_COEFFICIENT * dh * dh * Math.pow(reinforcementRho / RHO_ALUMINUM, 0.8);
  const fThr = lef > 0 ? 20 * Math.pow(d, 0.8) * Math.pow(lef, 0.9) : 0;
  return { fHead, fThr, faxRk: Math.min(ft, fThr, fHead) };
};

type ScrewFit = {
  t1: number;
  lt2: number;
  t2: number;
  leqreq: number;
  lef: number;
  a1min: number;
  a3c: number;
  a4t: number;
  a4c: number;
  stag: number;
  hmin: number;
  lmax: number;
  heightOK: boolean;
  geomOK: boolean;
  eBottom: number;
  eTop2: number;
  centered: boolean;
};

const computeScrewFit = (
  screw: Screw,
  b1: number,
  b2: number,
  h2: number,
  n1: 1 | 2,
  tipTolerance: number,
): ScrewFit => {
  const d = screw.screwFamily.diameter;
  const l = screw.length;
  const lf = screw.threadedLength;

  const t1 = b2;
  const lt2 = clamp0(l - b2);
  const t2 = clamp0(l - b2 - tipTolerance);
  const leqreq = Math.min(4 * d, 20 * d);
  const lef = leqreq >= Math.min(lt2, lf) ? 0 : Math.min(lt2, lf);
  const a1min = 0.7 * 5 * d;
  const a3c = 10 * d;
  const a4t = 7 * d;
  const a4c = 5 * d;
  const stag = n1 === 2 ? 2 * d : 0;
  const hmin = a4t + a4c + stag;
  const lmax = b1 + b2 - 10;
  const heightOK = h2 >= hmin;
  const geomOK = lef > 0 && l <= lmax && t2 > 0;
  const eBottom = Math.max(h2 / 2 - stag / 2, a4t);
  const eTop2 = eBottom + stag;
  const centered = h2 / 2 - stag / 2 >= a4t;

  return {
    t1,
    lt2,
    t2,
    leqreq,
    lef,
    a1min,
    a3c,
    a4t,
    a4c,
    stag,
    hmin,
    lmax,
    heightOK,
    geomOK,
    eBottom,
    eTop2,
    centered,
  };
};

// Geometry-only screw fit check (independent of screw spacing/loads), used to prune the
// screw picker down to lengths that can actually fit the current beam configuration.
export type ScrewFitCheckParams = {
  existingBase: number; // mm
  reinforcementBase: number; // mm
  reinforcementHeight: number; // mm
  reinforcementSides: 1 | 2;
  tipTolerance: number; // mm
};

export const isScrewGeometricallyValid = (screw: Screw, params: ScrewFitCheckParams): boolean => {
  const fit = computeScrewFit(
    screw,
    params.existingBase,
    params.reinforcementBase,
    params.reinforcementHeight,
    params.reinforcementSides,
    params.tipTolerance,
  );

  return fit.heightOK && fit.geomOK;
};

export const computeBeamReinforcement = (input: BeamReinforcementInput): BeamReinforcementResult | null => {
  const existing = input.existingMaterial;
  const reinforcement = input.reinforcementMaterial;
  const screw = input.screw;

  if (
    !input.existingBase ||
    !input.existingHeight ||
    !input.reinforcementBase ||
    !input.reinforcementHeight ||
    !input.beamLength ||
    !input.screwSpacing ||
    input.permanentLoad == null ||
    input.liveLoad == null
  ) {
    return null;
  }

  const b1 = input.existingBase;
  const h1 = input.existingHeight;
  const b2 = input.reinforcementBase;
  const h2 = input.reinforcementHeight;
  const n1 = input.reinforcementSides;
  const g2 = input.permanentLoad;
  const q1 = input.liveLoad;
  const beamLengthMm = input.beamLength * 1000;
  const a1ef = input.screwSpacing;
  const kmod = KMOD[input.serviceClass][input.loadDuration];

  // ---- Screw fit ----
  const d = screw.screwFamily.diameter;
  const my = screw.screwFamily.yieldMoment;
  const {
    t1,
    lt2,
    t2,
    leqreq,
    lef,
    a1min,
    a3c,
    a4t,
    a4c,
    stag,
    hmin,
    lmax,
    heightOK,
    geomOK,
    eBottom,
    eTop2,
    centered,
  } = computeScrewFit(screw, b1, b2, h2, n1, input.tipTolerance);

  const { fHead, fThr, faxRk } = axial(screw, lef, reinforcement.density);

  let fh1 = 0;
  let fh2 = 0;
  let johansenResult: JohansenResult | null = null;
  let fvRd = 0;
  if (t2 > 0) {
    fh1 = fhk(existing.density, d, input.preDrilled);
    fh2 = fhk(reinforcement.density, d, input.preDrilled);
    johansenResult = johansen(fh1, fh2, t1, t2, d, my, faxRk);
    fvRd = (kmod * johansenResult.min) / 1.4;
  }
  const fvRk = johansenResult ? johansenResult.min : 0;

  // ---- Loads (numerically, kN/m == N/mm, kept as in the original tool) ----
  const g1 = ((b1 * h1 * existing.density + n1 * b2 * h2 * reinforcement.density) * 1e-6 * 9.81) / 1000;
  const qd = 1.3 * g1 + 1.5 * g2 + 1.5 * q1;

  const i1 = (b1 * Math.pow(h1, 3)) / 12;
  const i2 = (b2 * Math.pow(h2, 3)) / 12;
  const ei1 = existing.modulusOfElasticity * i1;
  const ei2 = reinforcement.modulusOfElasticity * i2;
  const nu1 = ei1 / (ei1 + n1 * ei2);
  const nu2 = ei2 / (ei1 + n1 * ei2);

  const md = (qd * beamLengthMm * beamLengthMm) / 8;
  const md1 = md * nu1;
  const md2 = md * nu2;
  const w1 = (b1 * h1 * h1) / 6;
  const w2 = (b2 * h2 * h2) / 6;
  const sm1 = md1 / w1;
  const sm2 = md2 / w2;
  const fmd1 = (kmod * existing.bendingStrength) / 1.45;
  const fmd2 = (kmod * reinforcement.bendingStrength) / 1.45;
  const etaM1 = (sm1 / fmd1) * 100;
  const etaM2 = (sm2 / fmd2) * 100;

  const vd = (qd * beamLengthMm) / 2;
  const vd1 = vd * nu1;
  const vd2 = vd * nu2;
  const tau1 = (1.5 * vd1) / (0.67 * b1 * h1);
  const tau2 = (1.5 * vd2) / (0.67 * b2 * h2);
  const fvd1 = (kmod * existing.shearStrength) / 1.45;
  const fvd2 = (kmod * reinforcement.shearStrength) / 1.45;
  const etaV1 = (tau1 / fvd1) * 100;
  const etaV2 = (tau2 / fvd2) * 100;

  // Screws along the span (matches the plan-view layout of the original tool)
  const a3 = a3c;
  const a1 = Math.max(a1ef, 1);
  let nvBottom = 0;
  for (let x = a3; x <= beamLengthMm - a3 + 0.5; x += a1) nvBottom++;
  if (nvBottom < 2) nvBottom = 2;
  let nvTop = 0;
  if (n1 === 2) {
    for (let x = a3 + a1 / 2; x <= beamLengthMm - a3 + 0.5; x += a1) nvTop++;
  }

  const vEd = qd * nu2 * a1ef;
  const etaConn = fvRd > 0 ? (vEd / fvRd) * 100 : Infinity;

  const a4tEff = eBottom;
  const a4cEff = h2 - (n1 === 2 ? eTop2 : eBottom);
  const a4tOK = a4tEff >= a4t - 1e-9;
  const a4cOK = a4cEff >= a4c - 1e-9;
  const spacOK = a1ef >= a1min;
  const loadOK = Number.isFinite(etaConn) && etaConn <= 100;
  const screwOK = geomOK && heightOK && spacOK && loadOK;

  return {
    kmod,
    existing,
    reinforcement,
    screw,
    b1,
    h1,
    b2,
    h2,
    n1,
    screwSpacing: a1ef,
    tipTolerance: input.tipTolerance,
    preDrilled: input.preDrilled,
    g1,
    qd,
    i1,
    i2,
    nu1,
    nu2,
    md,
    md1,
    md2,
    w1,
    w2,
    sm1,
    sm2,
    fmd1,
    fmd2,
    etaM1,
    etaM2,
    etaMmax: Math.max(etaM1, etaM2),
    vd,
    vd1,
    vd2,
    tau1,
    tau2,
    fvd1,
    fvd2,
    etaV1,
    etaV2,
    etaVmax: Math.max(etaV1, etaV2),
    t1,
    lt2,
    t2,
    leqreq,
    lef,
    a1min,
    a3c,
    a4t,
    a4c,
    stag,
    hmin,
    lmax,
    heightOK,
    geomOK,
    eBottom,
    eTop2,
    centered,
    fh1,
    fh2,
    johansen: johansenResult,
    fvRd,
    fvRk,
    fHead,
    fThr,
    faxRk,
    nvBottom,
    nvTop,
    nvTotal: nvBottom + nvTop,
    vEd,
    etaConn,
    a4tEff,
    a4cEff,
    a4tOK,
    a4cOK,
    spacOK,
    loadOK,
    screwOK,
  };
};
